


















































































































































































































import { Component, Prop, PropSync, Watch, Vue } from "vue-property-decorator";
import store from "@/store";
//  @utilities
import moment from "moment";
import numberHelper from "Utilities/number-helper";
import htmlEncoder from "Utilities/htmlencode-helper";
// @types
import { Company } from "@/types/company";
//  @clients
import CompaniesClient from "Clients/companies-client";
//  @modules
import { getModule } from "vuex-module-decorators";
import CompaniesModule from "@/store/clients/Companies.module";
import SnackbarModule from "@/store/clients/Snackbar.module";

const Companies = getModule(CompaniesModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {},
})
export default class CompanyDataModal extends Vue {
  @PropSync("value", { required: true, type: Boolean, default: false }) isModalActive!: boolean;
  @Prop({ type: Array, default: () => [], required: true }) companies!: any[];
  @PropSync("companyToEditId", { type: Number, default: null }) syncCompanyToEditId!: number | null;
  @Prop({ type: String, default: "463px" }) width!: string;

  // @Data
  statusList: string[] = ["Active", "Suspended", "Terminated"];
  internalStatusSelected: string | null = "Active"; // default status
  formData: Partial<Company> = {};

  // @Watchers
  @Watch("isModalActive")
  onModalActiveChanged(isOpen: boolean): void {
    if (isOpen) this.initializeForm();
  }

  //  @Validations
  fieldRequired = {
    required: (value: any) => {
      // Allow 0 as valid value for numeric fields
      if (value === 0) return true;
      // Check if value exists (not null, undefined, or empty string)
      return !!value || "Field is required";
    },
    nameMaxLength: (value: any) => {
      // allow required() to handle empty - here validate length when present
      if (value === null || value === undefined || String(value).trim() === "") return true;
      return String(value).length <= 30 || "May not be longer than 30 characters";
    },
  };

  // @Getters
  get isNewCompany(): boolean {
    return this.syncCompanyToEditId === null;
  }

  get ModalMode(): any {
    return {
      mode: this.isNewCompany ? "add" : "edit",
      title: this.isNewCompany ? "Add Company" : "Edit Company",
      btnText: this.isNewCompany ? "Add Company" : "Update",
    };
  }

  get isSubmitDisabled(): boolean {
    // Use bound formData properties (template uses formData.*)
    const name = this.formData.name ?? "";

    const v = this.fieldRequired;

    // validator returns true (valid) or an error string -> treat only true as valid
    const ok = (validator: Function, value: any) => {
      try {
        return validator(value) === true;
      } catch {
        return false;
      }
    };

    const nameOk = ok(v.required, name) && ok(v.nameMaxLength, name);

    return !nameOk;
  }

  // @Methods
  // Initialize form data based on whether we're creating or editing
  initializeForm(): void {
    if (this.isNewCompany) {
      // New user: reset form with all fields initialized
      this.formData = {
        contactEmail: null,
        contactName: null,
        contactPhone: null,
        dcMasterDataCompanyId: null,
        name: "",
        notes: null,
        subscriptionStatus: "Active",
        vesselCount: 0,
      };
      this.internalStatusSelected = "Active";

      // Reset all field validations for add mode
      this.$nextTick(() => {
        this.$nextTick(() => {
          const form = this.$el.querySelector(".dfi__form");
          if (form) {
            const textFields = form.querySelectorAll(".v-text-field");
            textFields.forEach((field: any) => {
              if (field.__vue__ && field.__vue__.resetValidation) {
                field.__vue__.resetValidation();
              }
            });
          }
        });
      });
    } else {
      // Edit mode: populate form with user data
      const company = this.companies.find(c => c.id === this.syncCompanyToEditId);
      if (company) {
        // Clone company data and
        this.formData = {
          contactEmail: company.contactEmail || null,
          contactName: company.contactName || null,
          contactPhone: company.contactPhone || null,
          createdDate: company.createdDate || null,
          dcMasterDataCompanyId: company.dcMasterDataCompanyId || null,
          id: company.id || null,
          name: company.name || "",
          notes: company.notes || null,
          subscriptionStatus: company.subscriptionStatus || "Active",
          vesselCount: company.vesselCount || 0,
        };
        this.internalStatusSelected = company.subscriptionStatus || "Active";
      }
    }
  }

  close(): void {
    this.isModalActive = false;
    this.$emit("update:companyToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  cancel(): void {
    this.isModalActive = false;
    // Reset companyToEditId in parent when canceling
    this.$emit("update:companyToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  async submit(): Promise<void> {
    try {
      if (this.isNewCompany) {
        await CompaniesClient.create(this.formData);
      } else {
        this.formData = {
          ...this.formData,
          id: this.syncCompanyToEditId!,
        };
        await CompaniesClient.update(this.formData);
      }
      this.$emit("onSubmit", { userData: this.formData, isNewCompany: this.isNewCompany });
      Snackbar.showSnackbar({
        text: `Company <b>${htmlEncoder.htmlEncode(this.formData.name)}</b> successfully ${this.isNewCompany ? "created" : "updated"}`,
        color: "success",
      });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to submit company data" });
    } finally {
      this.isModalActive = false;
      // Reset companyToEditId in parent after submission
      this.$emit("update:companyToEditId", null);
      // Also reset form after submission
      setTimeout(() => {
        this.resetForm();
      }, 300);
    }
  }

  onStatusChange(): void {
    // Handle any additional logic when status changes, if needed
  }

  // Reset form data and validations
  resetForm(): void {
    // Reset form data to initial state
    this.formData = { subscriptionStatus: "Active" };
    this.internalStatusSelected = "Active";

    // Reset all field validations
    this.$nextTick(() => {
      const fields = ["name", "contactName", "contactPhone", "subscriptionStatus", "dcMasterDataCompanyId", "notes"];
      fields.forEach(fieldRef => {
        const field = (this.$refs as any)[fieldRef];
        if (field && field.resetValidation) {
          field.resetValidation();
        }
      });

      // Also reset v-text-fields in the form
      const form = this.$el?.querySelector?.(".dfi__form");
      if (form) {
        const textFields = form.querySelectorAll(".v-text-field");
        textFields.forEach((field: any) => {
          if (field.__vue__ && field.__vue__.resetValidation) {
            field.__vue__.resetValidation();
          }
        });
      }
    });
  }

  numericFilter(event: any): any {
    return numberHelper.numericRoundFilter(event);
  }
}
